import { Tab, TabWrapper, Tabs } from '@Components/Tabs';
import * as assetSchema from '@innovyze/lib_asset_schema';
import { selectAssetById } from '@Selectors/assetSummary.selectors';
import { NamespacedStoreState as MapUIStoreState } from '@Types/store.types';
import { InfoCard, PropertyCard } from '@innovyze/stylovyze';
import { AssetDetailsSummary } from '@Types';
import React, { useEffect, useMemo } from 'react';
import {
	displayUnit,
	extractFieldType,
	formatUserTableValue,
	getValue,
	mapSegments,
	mapUserTables,
} from './ValueMapping';

import {
	assetSchemaSelectorByType,
	getAssetSchemaAction,
} from '@innovyze/lib_asset_schema_store';
import { useGlobalization } from '@Translations';
import { a11yProps } from '@Utils';
import { getType } from './fieldType';
import { DefaultRootState, useDispatch, useSelector } from 'react-redux';
import { TabPanel } from '../../Tabs/TabPanel';

// Stands in for the category tabs on the 26 asset schemas that declare no
// Categories. Translated via t(section) like a real category name; 'General'
// is also a static key elsewhere in the repo, so the parser still emits it.
const GENERAL_SECTION = 'General';

const convertFieldType = (
	type: string,
	precision?: number,
): { fieldType: string; multiline?: boolean; precision?: number } => {
	switch (type) {
		case 'Boolean':
			return { fieldType: 'boolean' };
		case 'Double':
			return { fieldType: 'number', precision: precision ?? 2 };
		case 'Integer':
		case 'Long Integer':
			return { fieldType: 'number', precision: precision ?? 0 };
		case 'Date / Time':
			return { fieldType: 'date' };
		case 'Text':
			return { fieldType: 'string' };
		case 'Memo':
			return { fieldType: 'string', multiline: true };
		default:
			return { fieldType: 'string' };
	}
};

export const DetailsOverview = ({
	assetType,
	assetId,
}: {
	assetType: string;
	assetId: string;
}): JSX.Element => {
	const { t } = useGlobalization();
	const [value, setValue] = React.useState(0);
	const dispatch = useDispatch();

	useEffect(() => {
		if (assetType) {
			dispatch(getAssetSchemaAction({ assetType }));
		}
	}, [assetType, dispatch]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const assetSchema = useSelector<any, assetSchema.AssetSchema | undefined>(
		state => {
			if (assetType) {
				return assetSchemaSelectorByType(state, assetType);
			}
		},
	);

	const sections = assetSchema?.categories;

	const id = `${assetType}#${assetId}`;
	const details = useSelector<
		MapUIStoreState & DefaultRootState,
		AssetDetailsSummary | undefined
	>(store => selectAssetById(store, id));

	const segmentSchema = assetSchema
		?.getProperties()
		.find(p => p.id === 'segments') as
		| assetSchema.NestedProperty
		| undefined;

	const segments = useMemo(() => {
		if (!segmentSchema) return;
		const segments = details?.segments;
		if (!segments) return;
		return mapSegments(segments, t);
	}, [segmentSchema, details]);

	const userDefinedTables = useMemo(() => {
		if (!details?.userDefined) return;
		return mapUserTables(details.userDefined, details.userDefinedUnits);
	}, [details]);

	const handleChange = (event: unknown, newValue: number) => {
		setValue(newValue);
	};

	// The Segments and Custom Tables panels sit after the category tabs, so
	// their indices must count the tabs actually rendered. 26 of the asset
	// schemas declare no Categories - all of Stormwater among them - and a
	// single General tab stands in for them. `sections?.length ?? 1` put
	// Custom Tables at index 0 on those, colliding with that General panel,
	// and since MUI takes each tab's value from its position the Custom
	// Tables tab selected a value no panel answered to. (I360G-2486)
	const categorySections =
		sections && sections.length > 0 ? Array.from(sections) : undefined;
	// An absent `sections` means the schema has not loaded yet, which is not
	// the same as a schema that declares no categories: standing the General
	// tab in at that point makes it flash in and then vanish once the real
	// categories arrive, on the 48 schemas that do declare them.
	const standInSections = sections === undefined ? [] : [GENERAL_SECTION];
	const tabSections = categorySections ?? standInSections;
	const segmentsTabIndex = tabSections.length;
	const customTablesIndex = segments
		? segmentsTabIndex + 1
		: segmentsTabIndex;

	return (
		<InfoCard title={t('Properties')}>
			<TabWrapper>
				<Tabs
					orientation="vertical"
					variant="scrollable"
					value={value}
					onChange={handleChange}>
					{tabSections.map((section, i) => (
						<Tab key={i} label={t(section)} {...a11yProps(i)} />
					))}
					{segments && (
						<Tab
							label={t('Segments')}
							{...a11yProps(segmentsTabIndex)}
						/>
					)}
					{userDefinedTables && (
						<Tab
							label={t('Custom Tables')}
							{...a11yProps(customTablesIndex)}
						/>
					)}
				</Tabs>
				{tabSections.map((section, i) => (
					<TabPanel value={value} index={i} key={i}>
						<PropertyCard
							title={t(section)}
							properties={
								assetSchema
									?.getProperties()
									// With no categories declared, the single
									// General panel stands in for them and
									// holds every property.
									.filter(
										p =>
											!categorySections ||
											p.category === section,
									)
									.filter(p => p.type !== 'List')
									.map(
										p =>
											// TS analyser in IDE is smart enough, but the compiler isn't
											// so I have to tell ts the type.
											p as unknown as assetSchema.GenericSingleProperty,
									)
									.map(property => {
										const { fieldType, precision } =
											convertFieldType(
												property.type,
												property.precision,
											);
										return {
											title: t(property.displayName, {
												context: 'Asset properties',
											}),
											value: getValue(
												t,
												details,
												property.id,
												property.units,
											),
											cy: property.id,
											type: getType(
												fieldType,
												property.units,
												property.id,
											),
											decimalPlaces: precision,
											loading: !details,
											ellipsis: true,
										};
									}) ?? []
							}
							columns={4}
						/>
					</TabPanel>
				))}
				{segments && (
					<TabPanel value={value} index={segmentsTabIndex}>
						{segments.map(segment => (
							<PropertyCard
								key={segment.segmentId}
								title={t('Segment: {{segmentId}}', {
									segmentId: segment.segmentId,
								})}
								properties={
									segmentSchema?.properties
										.filter(p => p.id !== 'segmentId')
										.map(p => {
											const { fieldType, precision } =
												convertFieldType(
													p.type,
													p.precision,
												);
											return {
												title: t(p.displayName),
												value: getValue(
													t,
													segment,
													p.id,
													p.units,
												),
												cy: p.id,
												type: getType(
													fieldType,
													p.units,
													p.id,
												),
												decimalPlaces: precision,
												loading: !details,
											};
										}) ?? []
								}
								columns={4}
							/>
						))}
					</TabPanel>
				)}
				{userDefinedTables && (
					<TabPanel value={value} index={customTablesIndex}>
						{userDefinedTables.map(({ section, fields }) => (
							<PropertyCard
								key={section}
								title={section}
								properties={fields.map(
									({ name, value, unit }) => ({
										title: name,
										value: formatUserTableValue(
											value,
											unit,
										),
										cy: name,
										// The column name is deliberately not
										// passed as a property id - these are
										// user chosen CSV columns, so they
										// must not be matched against the
										// schema's year field names. The unit
										// goes through displayUnit so only
										// convertible ones reach the unit
										// display.
										type: getType(
											extractFieldType(value),
											displayUnit(unit),
										),
									}),
								)}
								columns={4}
							/>
						))}
					</TabPanel>
				)}
			</TabWrapper>
		</InfoCard>
	);
};
